import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, notification, Select } from 'antd';
import { shareCertificate } from '../../services/arcWelding';
import './ArcWelding.css';

const normalizeCertificates = (certificates, certificateId, certificateNo) => {
  if (Array.isArray(certificates) && certificates.length > 0) {
    return certificates
      .map((c) => ({
        id: c?.id,
        certificateNo: c?.certificateNo,
      }))
      .filter((c) => c.id);
  }
  if (certificateId) {
    return [{ id: certificateId, certificateNo }];
  }
  return [];
};

const ArcWeldingShareModal = ({
  open,
  certificates,
  certificateId,
  certificateNo,
  onClose,
}) => {
  const [emailList, setEmailList] = useState([]);
  const [sharing, setSharing] = useState(false);

  const targets = useMemo(
    () => normalizeCertificates(certificates, certificateId, certificateNo),
    [certificates, certificateId, certificateNo]
  );

  const targetIds = useMemo(() => targets.map((t) => t.id).join(','), [targets]);

  useEffect(() => {
    if (open) setEmailList([]);
  }, [open, targetIds]);

  const handleShare = async () => {
    const recipients = emailList.map((email) => String(email).trim()).filter(Boolean);
    if (!recipients.length) {
      notification.warning({ message: 'Please enter at least one email address' });
      return;
    }
    if (!targets.length) return;

    setSharing(true);
    try {
      const results = await Promise.all(
        targets.map((cert) => shareCertificate(cert.id, { emailTo: recipients }))
      );
      const sentCount = results.filter((r) => r?.emailSent !== false).length;
      notification.success({
        message: targets.length === 1
          ? (results[0]?.message || 'Certificate shared successfully via email')
          : `Shared ${sentCount} of ${targets.length} certificates`,
        description: `Sent to ${recipients.join(', ')}`,
      });
      onClose?.(true);
    } catch (err) {
      notification.error({
        message: 'Failed to share certificate',
        description: err?.response?.data?.message || err?.message || err?.error || String(err),
      });
    } finally {
      setSharing(false);
    }
  };

  const title = targets.length > 1
    ? `Share ${targets.length} Arc Welding Certificates`
    : 'Share Arc Welding Certificate';

  return (
    <Modal
      className="heatprofile-modal aw-modal-form aw-share-modal"
      title={title}
      open={open}
      onCancel={() => onClose?.(false)}
      centered
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={() => onClose?.(false)} disabled={sharing}>
          Cancel
        </Button>,
        <Button key="share" type="primary" onClick={handleShare} loading={sharing}>
          Share
        </Button>,
      ]}
    >
      {targets.length === 1 && targets[0].certificateNo && (
        <p className="aw-share-modal-cert">
          Certificate: <strong>{targets[0].certificateNo}</strong>
        </p>
      )}
      {targets.length > 1 && (
        <p className="aw-share-modal-cert">
          Certificates:{' '}
          <strong>
            {targets.map((t) => t.certificateNo || `ID ${t.id}`).join(', ')}
          </strong>
        </p>
      )}
      <div className="uploadFile_admin">
        <Select
          mode="tags"
          className="aw-share-email-select"
          placeholder="Enter email addresses"
          value={emailList}
          onChange={setEmailList}
          tokenSeparators={[',', ' ']}
          disabled={sharing}
          open={false}
        />
        <span className="aw-share-modal-hint">
          Type an email and press Enter, or paste comma-separated addresses
        </span>
      </div>
    </Modal>
  );
};

export default ArcWeldingShareModal;
